import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import { Button, Modal, Table } from "react-bootstrap";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import PageTitle from "../../layouts/PageTitle";
import {
  isPaymentAccessOwner,
  normalizePaymentEmail,
  usePaymentAccessScope,
} from "../../../hook/usePaymentAccessScope";
import {
  notifyError,
  notifyTopRight,
  notifyWarning,
} from "../../components/toast/SetToast";
import Toast from "../../components/toast/SetToast";

const emptyMapping = {
  email: "",
  displayName: "",
  providerRole: "sleep_technician",
  bookingName: "",
};

const PaymentAccessConfig = () => {
  const { currentUser } = useAuth();
  const {
    loading: accessLoading,
    paymentAccess,
  } = usePaymentAccessScope();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentAccessEmails, setPaymentAccessEmails] = useState([]);
  const [accessEmailInput, setAccessEmailInput] = useState("");
  const [pendingAccessEmailRemoval, setPendingAccessEmailRemoval] = useState(
    null,
  );
  const [mappings, setMappings] = useState([]);
  const [mappingForm, setMappingForm] = useState(emptyMapping);
  const [technicians, setTechnicians] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [technicianRecordNoteCheckEnabled, setTechnicianRecordNoteCheckEnabled] =
    useState(false);
  const [pendingTechnicianRecordNoteCheck, setPendingTechnicianRecordNoteCheck] =
    useState(null);

  const canManageFullAccessEmails = isPaymentAccessOwner(currentUser);

  const providerOptions = useMemo(() => {
    const providers =
      mappingForm.providerRole === "sleep_physician" ? doctors : technicians;

    return providers
      .map((provider) => ({
        name: String(provider.name || "").trim(),
        detail:
          provider.fullnameEn ||
          provider.fullnameTh ||
          provider.fullname ||
          provider.nickname ||
          "",
      }))
      .filter((provider) => provider.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [doctors, mappingForm.providerRole, technicians]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const [
        accessSnapshot,
        mappingSnapshot,
        doctorSnapshot,
        techSnapshot,
        technicianRecordNoteCheckSnapshot,
      ] =
        await Promise.all([
          getDocs(collection(db, "paymentAccessEmails")),
          getDocs(collection(db, "paymentUserMappings")),
          getDocs(collection(db, "doctors")),
          getDocs(collection(db, "sleeptechnicians")),
          getDoc(doc(db, "paymentAccessSettings", "technicianRecordNoteCheck")),
        ]);

      const emails = accessSnapshot.docs
        .map((accessDoc) =>
          normalizePaymentEmail((accessDoc.data() || {}).email || accessDoc.id),
        )
        .filter(Boolean);

      setPaymentAccessEmails([...new Set(emails)].sort());
      setMappings(
        mappingSnapshot.docs
          .map((mappingDoc) => ({ id: mappingDoc.id, ...mappingDoc.data() }))
          .sort((a, b) => String(a.email || a.id).localeCompare(String(b.email || b.id))),
      );
      setDoctors(
        doctorSnapshot.docs.map((doctorDoc) => ({
          id: doctorDoc.id,
          ...doctorDoc.data(),
        })),
      );
      setTechnicians(
        techSnapshot.docs.map((techDoc) => ({
          id: techDoc.id,
          ...techDoc.data(),
        })),
      );
      setTechnicianRecordNoteCheckEnabled(
        Boolean(
          technicianRecordNoteCheckSnapshot.exists() &&
            (technicianRecordNoteCheckSnapshot.data() || {}).enabled,
        ),
      );
    } catch (error) {
      console.error("Unable to load payment access configuration", error);
      notifyError("Unable to load payment access configuration", error.message || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paymentAccess) loadConfig();
  }, [paymentAccess]);

  if (accessLoading) {
    return (
      <Fragment>
        <Toast />
        <PageTitle activeMenu="Payment Access" motherMenu="Admin" />
        <div className="text-muted p-3">Checking payment access…</div>
      </Fragment>
    );
  }

  if (!paymentAccess) {
    return <Redirect to="/" />;
  }

  const addAccessEmail = async () => {
    if (!canManageFullAccessEmails) {
      notifyWarning("Only admin888 can change the full payment-access email list.");
      return;
    }

    const email = normalizePaymentEmail(accessEmailInput);
    if (!email || !email.includes("@")) {
      notifyWarning("Enter a valid account email.");
      return;
    }
    if (paymentAccessEmails.includes(email)) {
      notifyWarning("This account already has full payment access.");
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, "paymentAccessEmails", email), { email });
      setPaymentAccessEmails((current) => [...current, email].sort());
      setAccessEmailInput("");
      notifyTopRight("Full payment access added.");
    } catch (error) {
      console.error("Unable to add payment access email", error);
      notifyError("Unable to add payment access", error.message || error);
    } finally {
      setSaving(false);
    }
  };

  const removeAccessEmail = async (email) => {
    if (!canManageFullAccessEmails) {
      notifyWarning("Only admin888 can change the full payment-access email list.");
      return false;
    }

    setSaving(true);
    try {
      await deleteDoc(doc(db, "paymentAccessEmails", email));
      setPaymentAccessEmails((current) => current.filter((item) => item !== email));
      notifyTopRight("Full payment access removed.");
      return true;
    } catch (error) {
      console.error("Unable to remove payment access email", error);
      notifyError("Unable to remove payment access", error.message || error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const requestAccessEmailRemoval = (email) => {
    if (!canManageFullAccessEmails || saving) return;
    setPendingAccessEmailRemoval(email);
  };

  const closeAccessEmailRemovalConfirmation = () => {
    if (!saving) setPendingAccessEmailRemoval(null);
  };

  const confirmAccessEmailRemoval = async () => {
    if (!pendingAccessEmailRemoval) return;

    const removed = await removeAccessEmail(pendingAccessEmailRemoval);
    if (removed) setPendingAccessEmailRemoval(null);
  };

  const saveTechnicianRecordNoteCheck = async (enabled) => {
    setSaving(true);
    try {
      await setDoc(
        doc(db, "paymentAccessSettings", "technicianRecordNoteCheck"),
        {
          enabled,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.displayName || currentUser.email || "",
        },
        { merge: true },
      );
      setTechnicianRecordNoteCheckEnabled(enabled);
      notifyTopRight(
        enabled
          ? "Technician record-payment note check enabled."
          : "Technician record-payment note check disabled.",
      );
      return true;
    } catch (error) {
      console.error("Unable to save technician payment note check", error);
      notifyError("Unable to save payment note check", error.message || error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const requestTechnicianRecordNoteCheck = (enabled) => {
    if (saving || loading || enabled === technicianRecordNoteCheckEnabled) {
      return;
    }
    setPendingTechnicianRecordNoteCheck(enabled);
  };

  const closeTechnicianRecordNoteCheckConfirmation = () => {
    if (!saving) setPendingTechnicianRecordNoteCheck(null);
  };

  const confirmTechnicianRecordNoteCheck = async () => {
    if (pendingTechnicianRecordNoteCheck === null) return;

    const saved = await saveTechnicianRecordNoteCheck(
      pendingTechnicianRecordNoteCheck,
    );
    if (saved) setPendingTechnicianRecordNoteCheck(null);
  };

  const saveMapping = async () => {
    const email = normalizePaymentEmail(mappingForm.email);
    const bookingName = String(mappingForm.bookingName || "").trim();
    const displayName = String(mappingForm.displayName || "").trim();

    if (!email || !email.includes("@")) {
      notifyWarning("Enter the account email for this provider.");
      return;
    }
    if (!bookingName) {
      notifyWarning("Choose the exact provider name used in bookings.");
      return;
    }

    setSaving(true);
    try {
      await setDoc(
        doc(db, "paymentUserMappings", email),
        {
          email,
          displayName,
          providerRole: mappingForm.providerRole,
          bookingName,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      notifyTopRight("Payment provider mapping saved.");
      setMappingForm(emptyMapping);
      await loadConfig();
    } catch (error) {
      console.error("Unable to save payment user mapping", error);
      notifyError("Unable to save provider mapping", error.message || error);
    } finally {
      setSaving(false);
    }
  };

  const editMapping = (mapping) => {
    setMappingForm({
      email: mapping.email || mapping.id || "",
      displayName: mapping.displayName || "",
      providerRole:
        mapping.providerRole === "sleep_physician"
          ? "sleep_physician"
          : "sleep_technician",
      bookingName: mapping.bookingName || "",
    });
  };

  const removeMapping = async (mapping) => {
    if (!window.confirm(`Remove payment mapping for ${mapping.email || mapping.id}?`)) {
      return;
    }

    setSaving(true);
    try {
      await deleteDoc(doc(db, "paymentUserMappings", mapping.id));
      setMappings((current) => current.filter((item) => item.id !== mapping.id));
      if (normalizePaymentEmail(mappingForm.email) === mapping.id) {
        setMappingForm(emptyMapping);
      }
      notifyTopRight("Payment provider mapping removed.");
    } catch (error) {
      console.error("Unable to remove payment user mapping", error);
      notifyError("Unable to remove provider mapping", error.message || error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Toast />
      <PageTitle activeMenu="Payment Access" motherMenu="Admin" />

      <Modal
        centered
        show={Boolean(pendingAccessEmailRemoval)}
        onHide={closeAccessEmailRemovalConfirmation}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Remove full payment access?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Remove full payment access for{" "}
            <strong>{pendingAccessEmailRemoval}</strong>? This account will no
            longer be able to manage payment batches.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={closeAccessEmailRemovalConfirmation}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmAccessEmailRemoval}
            disabled={saving}
          >
            {saving ? "Removing…" : "Remove access"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={pendingTechnicianRecordNoteCheck !== null}
        onHide={closeTechnicianRecordNoteCheckConfirmation}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Confirm payment note-check change</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingTechnicianRecordNoteCheck ? (
            <p className="mb-0">
              Enable this check? Technician <strong>Record</strong> payments
              will require both <strong>Sleep Study Form</strong> and{" "}
              <strong>Consent Form</strong> notes. <strong>Score</strong>{" "}
              payments will remain available.
            </p>
          ) : (
            <p className="mb-0">
              Disable this check? Technician <strong>Record</strong> payments
              will no longer require Sleep Study Form and Consent Form notes.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={closeTechnicianRecordNoteCheckConfirmation}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant={pendingTechnicianRecordNoteCheck ? "success" : "danger"}
            onClick={confirmTechnicianRecordNoteCheck}
            disabled={saving}
          >
            {saving
              ? "Saving…"
              : pendingTechnicianRecordNoteCheck
                ? "Confirm and enable"
                : "Confirm and disable"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="card mb-4">
        <div className="card-header">
          <h4 className="card-title mb-0">
            Configured full payment-access emails ({paymentAccessEmails.length})
          </h4>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">
            <strong>admin888</strong> always has full access and does not need a
            document. Each other full-access account is stored as one document in
            <code className="ml-1">paymentAccessEmails</code> and can view,
            confirm, and manage payment batches for every technician and doctor.
          </p>
          {canManageFullAccessEmails ? (
            <div className="d-flex flex-wrap align-items-end mb-3">
              <div className="mr-2 mb-2" style={{ minWidth: 280 }}>
                <label className="mb-1">Account email</label>
                <input
                  type="email"
                  className="form-control"
                  value={accessEmailInput}
                  onChange={(event) => setAccessEmailInput(event.target.value)}
                  placeholder="name@example.com"
                />
              </div>
              <Button className="mb-2" onClick={addAccessEmail} disabled={saving}>
                Add full access
              </Button>
            </div>
          ) : (
            <p className="text-muted small">
              Only <strong>admin888</strong> can add or remove full payment-access
              emails.
            </p>
          )}
          {paymentAccessEmails.length ? (
            <div className="d-flex flex-wrap">
              {paymentAccessEmails.map((email) => (
                <span className="badge badge-primary mr-2 mb-2 p-2" key={email}>
                  {email}
                  {canManageFullAccessEmails && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-white p-0 ml-2"
                      onClick={() => requestAccessEmailRemoval(email)}
                      disabled={saving}
                      aria-label={`Remove ${email}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-muted">
              No full-access account emails are configured in paymentAccessEmails.
            </div>
          )}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h4 className="card-title mb-0">
            Technician record-payment note check
          </h4>
        </div>
        <div className="card-body">
          <div className="custom-control custom-switch mb-3">
            <input
              id="technician-record-note-check"
              type="checkbox"
              className="custom-control-input"
              checked={technicianRecordNoteCheckEnabled}
              onChange={(event) =>
                requestTechnicianRecordNoteCheck(event.target.checked)
              }
              disabled={saving || loading}
            />
            <label
              className="custom-control-label"
              htmlFor="technician-record-note-check"
            >
              Require Sleep Study Form and Consent Form before Record payment
            </label>
          </div>
          <p className="text-muted mb-2">
            When enabled, a technician can select <strong>Record</strong> only
            when both patient notes are present. <strong>Score</strong> remains
            available. Doctor payments are not checked.
          </p>
          <span
            className={`badge ${
              technicianRecordNoteCheckEnabled
                ? "badge-success"
                : "badge-secondary"
            }`}
          >
            {technicianRecordNoteCheckEnabled ? "Enabled" : "Disabled"}
          </span>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h4 className="card-title mb-0">Provider-to-booking name mapping</h4>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">
            Use this when a provider&apos;s account display name differs from the
            name stored in booking data. Neither the account&apos;s role nor the
            suggested provider type below limits the mapping; that type only
            filters the booking-name suggestions. Restricted users can only
            fetch cases whose booking name matches this exact value.
          </p>
          <div className="row">
            <div className="col-12 col-md-6 col-lg-3 mb-3">
              <label className="mb-1">Account email</label>
              <input
                type="email"
                className="form-control"
                value={mappingForm.email}
                onChange={(event) =>
                  setMappingForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                placeholder="name@example.com"
              />
            </div>
            <div className="col-12 col-md-6 col-lg-3 mb-3">
              <label className="mb-1">Account display name (reference)</label>
              <input
                type="text"
                className="form-control"
                value={mappingForm.displayName}
                onChange={(event) =>
                  setMappingForm((current) => ({
                    ...current,
                    displayName: event.target.value,
                  }))
                }
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2 mb-3">
              <label className="mb-1">Suggested provider type</label>
              <select
                className="form-control"
                value={mappingForm.providerRole}
                onChange={(event) =>
                  setMappingForm((current) => ({
                    ...current,
                    providerRole: event.target.value,
                    bookingName: "",
                  }))
                }
              >
                <option value="sleep_technician">Sleep technician</option>
                <option value="sleep_physician">Sleep physician</option>
              </select>
            </div>
            <div className="col-12 col-md-6 col-lg-4 mb-3">
              <label className="mb-1">Exact name in booking data</label>
              <input
                type="text"
                className="form-control"
                list="payment-booking-name-options"
                value={mappingForm.bookingName}
                onChange={(event) =>
                  setMappingForm((current) => ({
                    ...current,
                    bookingName: event.target.value,
                  }))
                }
                placeholder="Select or enter the booking name"
              />
              <datalist id="payment-booking-name-options">
                {providerOptions.map((provider) => (
                  <option key={provider.name} value={provider.name}>
                    {provider.name}
                    {provider.detail ? ` — ${provider.detail}` : ""}
                  </option>
                ))}
              </datalist>
              <small className="form-text text-muted">
                Choose a listed provider or enter the exact value already stored
                in the booking record.
              </small>
            </div>
          </div>
          <div>
            <Button onClick={saveMapping} disabled={saving}>
              Save mapping
            </Button>
            <Button
              variant="light"
              className="ml-2"
              onClick={() => setMappingForm(emptyMapping)}
              disabled={saving}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">Configured provider mappings</h4>
          <Button variant="light" size="sm" onClick={loadConfig} disabled={loading || saving}>
            Refresh
          </Button>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-3 text-muted">Loading configuration…</div>
          ) : mappings.length ? (
            <Table responsive className="table-striped mb-0">
              <thead>
                <tr>
                  <th>Account email</th>
                  <th>Display name</th>
                  <th>Suggested provider type</th>
                  <th>Booking name</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping) => (
                  <tr key={mapping.id}>
                    <td>{mapping.email || mapping.id}</td>
                    <td>{mapping.displayName || "-"}</td>
                    <td>
                      {mapping.providerRole === "sleep_physician"
                        ? "Sleep physician"
                        : "Sleep technician"}
                    </td>
                    <td>{mapping.bookingName || "-"}</td>
                    <td className="text-right">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="mr-2"
                        onClick={() => editMapping(mapping)}
                        disabled={saving}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeMapping(mapping)}
                        disabled={saving}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="p-3 text-muted">No provider mappings configured.</div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default PaymentAccessConfig;
